import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Settings2, Plus, Trash2, RotateCcw, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

const FORMS = [["", "No linked form"], ["goal-worksheet", "Goal-Setting Worksheet"], ["training-record", "Training Record"], ["self-assessment", "Employee Self-Assessment"], ["perf-review", "Performance Review"], ["performance-plan", "Performance Plan"]];

export default function OnboardingTemplateEditor() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/onboarding/template").then((r) => { setData(r.data); setItems(r.data.items.map((i) => ({ ...i }))); }).catch(() => toast.error("Could not load template"));
  useEffect(() => { if (open) load(); }, [open]);

  const update = (idx, patch) => setItems((it) => it.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  const remove = (idx) => setItems((it) => it.filter((_, i) => i !== idx));
  const add = (phase) => setItems((it) => [...it, { phase, title: "", description: "", owner: "employee", form_id: null }]);
  const save = async () => {
    const clean = items.filter((i) => i.title.trim().length >= 2).map((i) => ({ ...i, form_id: i.form_id || null, description: i.description || "" }));
    if (!clean.length) return toast.error("Add at least one item");
    setBusy(true);
    try { await api.put("/onboarding/template", { items: clean }); toast.success("Master template saved — new plans will start from it"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset the master template to the LUMIS default checklist?")) return;
    try { await api.delete("/onboarding/template"); toast.success("Template reset to default"); load(); } catch { toast.error("Could not reset"); }
  };

  return (
    <>
      <Button data-testid="template-editor-btn" variant="outline" onClick={() => setOpen(true)} className="border-slate-300"><Settings2 className="w-4 h-4 mr-2" /> Edit master template</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-display">Master 30/60/90 onboarding template</DialogTitle>
            <DialogDescription>Every new onboarding plan starts from this checklist. Existing plans are not changed. {data?.customized ? `Customized · last saved by ${data.updated_by_name}` : "Currently the LUMIS default."}</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-5 pr-1">
            {(data?.phases || []).map((ph) => (
              <div key={ph.key} data-testid={`template-phase-${ph.key}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-display font-bold text-[#0A2A5E] text-sm">{ph.title} <span className="text-slate-400 font-normal">· due day {ph.days}</span></div>
                  <Button size="sm" variant="ghost" data-testid={`template-add-${ph.key}`} onClick={() => add(ph.key)} className="h-7 text-xs text-[#7C3F00]"><Plus className="w-3.5 h-3.5 mr-1" /> Add item</Button>
                </div>
                <div className="space-y-2">
                  {items.map((it, idx) => it.phase === ph.key && (
                    <div key={idx} data-testid={`template-item-${idx}`} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_8rem_10rem_auto] gap-2 items-center p-2 rounded-lg border border-slate-200 bg-white">
                      <Input data-testid={`template-title-${idx}`} value={it.title} onChange={(e) => update(idx, { title: e.target.value })} placeholder="Item title" className="h-8 text-xs" />
                      <Input value={it.description || ""} onChange={(e) => update(idx, { description: e.target.value })} placeholder="Short description" className="h-8 text-xs" />
                      <Select value={it.owner} onValueChange={(v) => update(idx, { owner: v })}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="employee">New hire</SelectItem><SelectItem value="manager">Manager</SelectItem></SelectContent>
                      </Select>
                      <Select value={it.form_id || "none"} onValueChange={(v) => update(idx, { form_id: v === "none" ? null : v })}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="No linked form" /></SelectTrigger>
                        <SelectContent>{FORMS.map(([v, l]) => <SelectItem key={v || "none"} value={v || "none"}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                      <button data-testid={`template-remove-${idx}`} onClick={() => remove(idx)} className="text-slate-300 hover:text-rose-600 justify-self-end" aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap justify-between gap-2 pt-2 border-t border-slate-200">
            <Button variant="ghost" size="sm" data-testid="template-reset" onClick={reset} className="text-rose-600 text-xs"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset to LUMIS default</Button>
            <div className="flex gap-2">
              <span className="text-xs text-slate-500 self-center">{items.length} items</span>
              <Button data-testid="template-save" onClick={save} disabled={busy} className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save template</>}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
